import type React from "react";
import { useState } from "react";
import { flushSync } from "react-dom";
// eslint-disable-next-line no-restricted-imports -- this file wraps this restricted library
import useResizeObserverLib from "use-resize-observer";

/**
 * Store and update an element's size using a ResizeObserver, attached using
 * the returned ref callback. The `width` and `height` will be `undefined` on
 * the first render. By default, size will be updated using React's typical
 * batching mechanisms. To re-render synchronously before the next frame is
 * displayed, set the `sync` option to `true` which will wrap the state update
 * with ReactDOM's `flushSync` function.
 */
export function useResizeObserver({ sync = false } = {}): {
  ref: React.RefCallback<Element>;
  width: number | undefined;
  height: number | undefined;
} {
  const [{ width, height }, setSize] = useState<{
    width: number | undefined;
    height: number | undefined;
  }>({
    width: undefined,
    height: undefined,
  });

  const { ref } = useResizeObserverLib({
    onResize(newSize) {
      if (sync) {
        flushSync(() => {
          setSize(newSize);
        });
      } else {
        setSize(newSize);
      }
    },
  });

  return { ref, width, height };
}
